import { useTranslation } from 'react-i18next';

import { getIntentCategoryFromParam } from './intentConfig';

/**
 * Maps intent parameters to translation categories
 * This allows for flexible intent matching - multiple intents can map to the same category
 */
const getIntentCategory = (intent: string): string | null => {
  return getIntentCategoryFromParam(intent);
};

/**
 * Hook to get intent-specific translations with fallback to default translations
 * @param intent - The intent parameter from URL (e.g., 'video-download')
 * @param baseKey - The base translation key (e.g., 'importURL')
 * @returns Translation function that checks for intent-specific translations first
 */
export const useIntentTranslation = (
  intent: string | null,
  baseKey: string
) => {
  const { t } = useTranslation();

  const getIntentTranslation = (key: string): string => {
    if (!intent) {
      return t(`${baseKey}.${key}`);
    }

    // Check if intent contains specific keywords and map to intent categories
    const intentCategory = getIntentCategory(intent);

    if (intentCategory) {
      // Try intent-specific translation first
      const intentKey = `${baseKey}.intents.${intentCategory}.${key}`;
      const intentTranslation = t(intentKey);

      // If intent translation exists and is different from the key, use it
      if (intentTranslation !== intentKey) {
        return intentTranslation;
      }
    }

    // Fallback to default translation
    return t(`${baseKey}.${key}`);
  };

  return { t: getIntentTranslation, originalT: t };
};

/**
 * Utility to extract intent from URL parameters
 */
export const getIntentFromUrl = (): string | null => {
  const hashPart = window.location.hash.split('?')[1] || '';
  const urlParams = new URLSearchParams(hashPart);
  return urlParams.get('intent');
};
